import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

// Initialize PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const API_BASE_URL = process.env.REACT_APP_API_URL;

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState('');
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfError, setPdfError] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/admin/products/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        setProduct(response.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Error fetching product details');
      }
    };
    fetchProduct();
  }, [id]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfError(null);
  };

  const onDocumentLoadError = (error) => {
    console.error('Error loading PDF:', error);
    setPdfError('Error loading PDF. Please try again.');
  };

  const handleCompanyClick = async (company) => {
    console.log('Company data:', company); // For debugging
    if (!company.file_name) {
      setPdfError('PDF file name not found for this company');
      return;
    }
    
    try {
      // Verify the PDF exists first
      const response = await axios.head(`${API_BASE_URL}/pdfs/datas/${company.file_name}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.status === 200) {
        setSelectedPdf(company.file_name);
        setPageNumber(1);
        setPdfError(null);
      }
    } catch (err) {
      console.error('Error accessing PDF:', err);
      setPdfError('Unable to access the PDF file. It may not exist or you may not have permission.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <nav className="bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/admin')}
                className="text-gray-300 hover:text-yellow-500 transition-colors duration-200 flex items-center gap-2"
              >
                <ArrowLeftIcon className="h-5 w-5" />
                Back to Products
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {error && (
            <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {pdfError && (
            <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded mb-4">
              {pdfError}
            </div>
          )}

          {product && (
            <div>
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-6">
                <h1 className="text-2xl font-bold text-yellow-500 mb-4">{product.name}</h1>
                
                <h2 className="text-lg font-semibold text-gray-300 mb-3">Associated Companies</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(() => {
                    try {
                      const companiesData = typeof product.companies === 'string' 
                        ? JSON.parse(product.companies)
                        : product.companies;

                      if (!companiesData || companiesData.length === 0) {
                        return (
                          <div className="col-span-full text-center text-gray-400 py-4">
                            No companies associated with this product
                          </div>
                        );
                      }

                      return companiesData.map((company) => (
                        <div
                          key={company.id}
                          className="bg-gray-700 p-4 rounded-md border border-gray-600 cursor-pointer hover:border-yellow-500 transition-colors duration-200"
                          onClick={() => handleCompanyClick(company)}
                        >
                          <h3 className="text-yellow-500 font-medium mb-2">{company.name}</h3>
                          <p className="text-sm text-gray-400">
                            {company.file_name ? 'Click to view PDF' : 'No PDF available'}
                          </p>
                        </div>
                      ));
                    } catch (err) {
                      console.error('Error parsing companies data:', err);
                      return (
                        <div className="col-span-full text-center text-red-400 py-4">
                          Error displaying companies data
                        </div>
                      );
                    }
                  })()}
                </div>
              </div>

              {selectedPdf && (
                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <Document
                    file={`${API_BASE_URL}/pdfs/datas/${selectedPdf}`}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    className="max-w-full"
                    loading={
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500 mx-auto"></div>
                        <p className="mt-2 text-gray-400">Loading PDF...</p>
                      </div>
                    }
                  >
                    <Page 
                      pageNumber={pageNumber} 
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      className="max-w-full"
                      width={Math.min(window.innerWidth - 48, 800)}
                    />
                  </Document>
                  
                  {numPages && (
                    <div className="flex justify-center items-center mt-4 space-x-4">
                      <button
                        onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                        disabled={pageNumber <= 1}
                        className="px-4 py-2 bg-gray-600 text-gray-200 rounded-md hover:bg-gray-500 transition-colors duration-200 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="text-gray-300">
                        Page {pageNumber} of {numPages}
                      </span>
                      <button
                        onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
                        disabled={pageNumber >= numPages}
                        className="px-4 py-2 bg-gray-600 text-gray-200 rounded-md hover:bg-gray-500 transition-colors duration-200 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ProductDetails;
